import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { CopyToClipboard } from 'react-copy-to-clipboard';
import { PDFDownloadButton, PDFViewButton } from './srScaleFixationPDFUI';
import LoadingPage from '../../Loader';
import ErrorPage from '../../pages/dataSubmit/404PageNotFound';
import SubmissionConfirmation from '../../pages/dataSubmit/dataSubmitted';
import { FiCopy, FiCheck, FiDownload, FiEye, FiPrinter } from 'react-icons/fi';
import { motion, AnimatePresence } from 'framer-motion';
import { InvoiceDownloadButton, InvoiceViewButton } from '../../pages/payment/Invoice/InvoicePDFButtons';

const formatDate = (dateString) => {
  if (!dateString) return "N/A";
  const date = new Date(dateString);
  return date.toLocaleDateString("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
};

const SrScaleFixationResult = () => {
  const { uniqueFormId } = useParams();
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [httpStatus, setHttpStatus] = useState(null);
  const [timestamp, setTimestamp] = useState(null);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const response = await fetch(
          `${process.env.REACT_APP_BACKEND_URL}/api/srScaleFixation/srScaleFixation-result/${uniqueFormId}`
        );

        setHttpStatus(response.status);

        if (response.status === 403) {
          const data = await response.json();
          setTimestamp(data.timestamp); 
          return; 
        }

        if (!response.ok) throw new Error('Failed to fetch data');
        const data = await response.json();
        setResult(data.result || null);
        
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [uniqueFormId]);

  if (loading) return <LoadingPage />;
  if (httpStatus === 403) {
    return <SubmissionConfirmation uniqueFormId={uniqueFormId} timestamp={timestamp} />;
  }
  if (httpStatus === 404) return <ErrorPage/>;

  return (
    <div className="min-h-screen bg-gray-50 py-32 px-6 lg:px-12 xl:px-20">
      <div className="max-w-screen-7xl mx-auto space-y-8">
        <div className="bg-white shadow-sm rounded-lg border border-gray-200">
          <div className="px-8 py-6">
            <h1 className="text-2xl font-semibold text-[#4CAF50]">Senior Scale Fixation Result</h1>
            <p className="text-[#4CAF50] text-sm">Accompaniment to Government Resolution, Higher and Technical Education Department</p>
          </div>
          <div className="px-8 py-4 border-t border-gray-200 flex flex-col lg:flex-row justify-between items-start lg:items-center">
            <div>
              <h2 className="text-sm font-medium text-gray-700">Application Reference</h2>
              <p className="text-xs text-gray-500">Keep this ID for future reference</p>
            </div>
            <div className="flex items-center space-x-2 mt-3 lg:mt-0">
              <span className="font-mono text-gray-800 text-sm bg-gray-100 px-3 py-1 rounded">{uniqueFormId}</span>
              <CopyToClipboard 
                text={uniqueFormId}
                onCopy={() => {
                  setCopied(true);
                  setTimeout(() => setCopied(false), 2000);
                }}
              >
                <button className="p-2 text-gray-600 hover:text-[#4CAF50] rounded hover:bg-gray-100">
                  {copied ? <FiCheck className="text-[#4CAF50]" /> : <FiCopy size={16} />}
                </button>
              </CopyToClipboard>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 xl:grid-cols-3 gap-8">
          <div className="xl:col-span-2 space-y-8">
            <div className="bg-white p-8 shadow-sm rounded-lg border border-gray-200">
              <h2 className="text-xl font-semibold text-gray-800 mb-4">Basic Information</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div><h3 className="text-sm text-gray-500">Institution</h3><p className="text-gray-900 font-medium">{result.officeName || 'N/A'}</p></div>
                <div><h3 className="text-sm text-gray-500">Employee Name</h3><p className="text-gray-900 font-medium">{result.employeeName || 'N/A'}</p></div>
                <div><h3 className="text-sm text-gray-500">Designation (6th Pay)</h3><p className="text-gray-900">{result.designation16 || 'N/A'}</p></div>
                <div><h3 className="text-sm text-gray-500">Date of Joining</h3><p className="text-gray-900">{formatDate(result.dateOfJoining)}</p></div>
              </div>
            </div>

            <div className="bg-white p-8 shadow-sm rounded-lg border border-gray-200">
              <h2 className="text-xl font-semibold text-gray-800 mb-4">Pay Details</h2>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <div className="bg-green-50 p-4 rounded-lg border-l-4 border-[#4CAF50]">
                  <h3 className="text-xs text-gray-600 uppercase">Current Pay Band</h3>
                  <p className="text-gray-900 font-medium">{result.payBand || 'N/A'}</p>
                  <p className="text-xs text-gray-600">Basic Pay: {result.basicPay || 'N/A'}</p>
                </div>
                <div className="bg-green-50 p-4 rounded-lg border-l-4 border-[#4CAF50]">
                  <h3 className="text-xs text-gray-600 uppercase">Senior Scale</h3>
                  <p className="text-gray-900 font-medium">{result.seniorScale || 'N/A'}</p>
                  <p className="text-xs text-gray-600">Grade Pay: {result.gradePay || 'N/A'}</p>
                </div>
                <div className="bg-green-50 p-4 rounded-lg border-l-4 border-[#4CAF50]">
                  <h3 className="text-xs text-gray-600 uppercase">Promotion Details</h3>
                  <p className="text-gray-900 font-medium">{result.designationPromotion || 'N/A'}</p>
                  <p className="text-xs text-gray-600">Date: {formatDate(result.dateOfPromotion)}</p>
                </div>
              </div>
            </div>

            <div className="bg-white p-8 shadow-sm rounded-lg border border-gray-200">
              <h2 className="text-xl font-semibold text-gray-800 mb-4">Service Details</h2>
              <div className="space-y-4">
                <div><h3 className="text-sm text-gray-600">Unpaid Leave</h3><p className="text-gray-900">{result.hasUnpaidLeave === 'Yes' ? result.unpaidLeaveDetails || 'Details not provided' : 'No unpaid leave'}</p></div>
                <div><h3 className="text-sm text-gray-600">Service Break</h3><p className="text-gray-900">{result.hasServiceBreak === 'Yes' ? `${result.serviceBreakDetails || 'Details not provided'}${result.isServiceBreakCondoned === 'Yes' ? ' (Condoned)' : ''}` : 'No service break'}</p></div>
                <div><h3 className="text-sm text-gray-600">Training Details</h3><p className="text-gray-900">{result.trainingLocation ? `${result.trainingLocation} (${result.trainingDuration})` : 'No training records'}</p></div>
              </div>
            </div>
          </div>

          <div className="space-y-8">
                    <div className="bg-white p-8 shadow-sm rounded-lg border border-gray-200">
                <div className="mb-6">
                    <p className="text-sm text-gray-600 mb-6">
                    Access and manage your official documents and invoices
                    </p>

                    <div className="space-y-4">
                    {/* Appointment Letter PDF Download */}
                    <motion.div whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }}>
                        <PDFDownloadButton result={result} />
                    </motion.div>
                    {/* View (Print) All Documents */}
                    <motion.div whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }}>
                        <PDFViewButton result={result} />
                    </motion.div>

                    {/* Invoice Download Button */}
                    <motion.div whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }}>
                    <InvoiceDownloadButton 
                        employeeName={result.employeeName} 
                        formName='New Appointment High School'
                    amount={299}  
                        transactionID={result.transactionID}
                        timestamp={result.timestamp}
                    />
                    </motion.div>

                    {/* Invoice View Button */}
                    <motion.div whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }}>
                        <InvoiceViewButton 
                    employeeName={result.employeeName || ''} 
                        formName='New Appointment High School'
                    amount={299}  
                        transactionID={result.transactionID}
                        timestamp={result.timestamp}
                    />
                    </motion.div>

                    </div>
                </div>
                </div>

            <div className="bg-white p-6 shadow-sm rounded-lg border border-gray-200">
              <h3 className="text-sm font-medium text-gray-800 mb-2">Important Information</h3>
              <p className="text-xs text-gray-600">This is an official document. Please save the reference ID for future correspondence. For any queries, contact the administration office.</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SrScaleFixationResult;